import React from 'react';
import {safelySetInnerHTML} from '@udemy-v2/utils';
import {ShowMore} from '@udemy-v2/react-reveal-components';
import {useI18n} from '@udemy-v2/i18n';
import classNames from 'classnames';

import styles from './instructor-description.module.scss';

export interface InstructorDescriptionProps {
    description: string;
}

/**
 * InstructorDescription component displays a brief description of the instructor.
 *
 * @component
 * @example
 * ```jsx
 * <InstructorDescription description="This is a brief description of the instructor." />
 * ```
 *
 * @param {InstructorDescriptionProps} props - The properties for the component.
 * @param {string} props.description - A brief description of the instructor.
 *
 * @returns {JSX.Element} A React component that renders the instructor's description.
 */
export const InstructorDescription = ({description}: InstructorDescriptionProps) => {
    const {gettext} = useI18n();
    return (
        <div className={classNames('ud-text-sm', styles.description)}>
            <ShowMore
                ariaLabelExpanded={gettext('Show less about the instructor')}
                ariaLabelCollapsed={gettext('Show more about the instructor')}
                collapsedHeight={146}
                withGradient={true}
            >
                <div
                    {...safelySetInnerHTML({
                        descriptionOfCaller: 'instructor-description:description-content',
                        html: description,
                        dataPurpose: 'description-content',
                    })}
                />
            </ShowMore>
        </div>
    );
};
